import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, CheckCircle2, Home as HomeIcon, MessageSquare, CalendarCheck, ShieldCheck } from "lucide-react";
import { api, priceLabel, fmtDate } from "@/lib/loop";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { VerificationBadge } from "@/components/Badges";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChatInbox } from "@/components/Chat";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";

const ENQ_STATUS = ["new", "contacted", "site_visit_scheduled", "negotiation", "closed"];

export default function OwnerDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [props, setProps] = useState([]);
  const [enq, setEnq] = useState([]);

  const load = () => {
    api.get("/my/properties").then((r) => setProps(r.data));
    api.get("/owner/enquiries").then((r) => setEnq(r.data));
  };
  useEffect(() => { load(); }, []);

  const confirm = async (id) => { await api.post(`/properties/${id}/confirm-availability`); toast.success("Availability confirmed & listing published"); load(); };
  const setStatus = async (id, status) => { await api.put(`/properties/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  const setEnqStatus = async (id, status) => { await api.put(`/enquiries/${id}/status`, { status }); toast.success("Enquiry updated"); load(); };

  const stat = (label, val, Icon) => (
    <div className="bg-[#0B132B] border border-white/10 rounded-2xl p-5">
      <Icon className="w-5 h-5 text-[#C5A059]" />
      <div className="mt-3 text-2xl font-extrabold text-white">{val}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 pb-28 md:pb-16">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">Owner Dashboard</div>
          <h1 className="mt-1 text-2xl font-bold text-white">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Button data-testid="add-property-btn" onClick={() => nav("/list-property")} className="bg-[#C5A059] hover:bg-[#b18f47] text-[#070C1E] font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Add Property
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stat("Your properties", props.length, HomeIcon)}
        {stat("Published", props.filter((p) => p.published).length, CheckCircle2)}
        {stat("Enquiries", enq.length, MessageSquare)}
        {stat("Site visits", enq.filter((e) => e.type === "site_visit").length, CalendarCheck)}
      </div>

      <Tabs defaultValue="properties" className="mt-8">
        <TabsList className="bg-[#0B132B] border border-white/10">
          <TabsTrigger value="properties" data-testid="owner-tab-properties" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300">Properties</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="owner-tab-enquiries" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300">Enquiries & Visits</TabsTrigger>
          <TabsTrigger value="messages" data-testid="owner-tab-messages" className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#070C1E] text-slate-300"><MessageCircle className="w-4 h-4 mr-1.5" /> Messages</TabsTrigger>
        </TabsList>

        <TabsContent value="properties" className="mt-6 space-y-4">
          {props.length === 0 && <div className="text-slate-500 py-12 text-center">No properties yet. Click "Add Property" to list one.</div>}
          {props.map((p) => (
            <div key={p.id} data-testid={`owner-property-${p.loop_id}`} className="bg-[#0B132B] border border-white/10 rounded-2xl p-5 flex flex-col lg:flex-row gap-5">
              <img src={p.images?.[0]} alt="" className="w-full lg:w-48 h-32 object-cover rounded-xl" />
              <div className="flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-white cursor-pointer hover:text-[#C5A059]" onClick={() => nav(`/property/${p.id}`)}>{p.title}</h3>
                    <div className="font-mono-loop text-xs text-slate-500 mt-0.5">{p.loop_id}</div>
                  </div>
                  <VerificationBadge status={p.verification_status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                  <span>{priceLabel(p)}</span>
                  <span className={p.published ? "text-emerald-400" : "text-amber-400"}>
                    {p.published ? "🟢 Published" : "🟡 " + (p.moderation === "verification_pending" ? "Verification Pending" : "Unpublished")}
                  </span>
                  <span>Last verified: {fmtDate(p.last_verified)}</span>
                  <span className="capitalize">Availability: {p.status.replace("_", " ")}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid={`confirm-avail-${p.loop_id}`} size="sm" onClick={() => confirm(p.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                    <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Confirm Still Available
                  </Button>
                  <Select onValueChange={(v) => setStatus(p.id, v)}>
                    <SelectTrigger className="h-8 w-36 bg-[#131B35] border-white/10 text-white text-xs" data-testid={`status-select-${p.loop_id}`}><SelectValue placeholder="Update status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="under_offer">Under Offer</SelectItem>
                      <SelectItem value="sold">Mark Sold</SelectItem>
                      <SelectItem value="rented">Mark Rented</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6 space-y-3">
          {enq.length === 0 && <div className="text-slate-500 py-12 text-center">No enquiries yet.</div>}
          {enq.map((e) => (
            <div key={e.id} className="bg-[#0B132B] border border-white/10 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-white capitalize">{e.type.replace("_", " ")} · {e.property_title}</div>
                  <div className="text-xs text-slate-500">{e.buyer_name} · {fmtDate(e.created_at)}</div>
                  {e.message && <p className="mt-2 text-sm text-slate-400">"{e.message}"</p>}
                  {e.visit_date && <p className="mt-1 text-xs text-[#C5A059]">Requested visit: {e.visit_date} {e.visit_time} · {e.visitors} visitor(s)</p>}
                </div>
                <Select value={e.status} onValueChange={(v) => setEnqStatus(e.id, v)}>
                  <SelectTrigger className="h-8 w-44 bg-[#131B35] border-white/10 text-white text-xs" data-testid={`enq-status-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ENQ_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="messages" className="mt-6">
          <ChatInbox me={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
